import { Search as SearchIcon, Flag } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Input as SearchInput } from "@/components/ui/input";
import React, { useEffect, useState } from "react";
import { useLang } from "@/i18n";
import { money } from "@/lib/constants";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { onPendingChange, pendingRequests } from "@/lib/api";

export function RowSearch({ value, onChange, placeholder, testid }) {
  return (
    <div className="relative w-64">
      <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <SearchInput value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="pl-8" data-testid={testid} />
    </div>
  );
}

// Zoekterm uit de URL (?q=) zodat het globale zoekveld gefilterd doorlinkt, ook zonder herlading
export function useQueryFilter() {
  const [params] = useSearchParams();
  const urlQ = params.get("q") || "";
  const [q, setQ] = useState(urlQ);
  useEffect(() => { setQ(urlQ); }, [urlQ]);
  return [q, setQ];
}

// Een nieuwe tussentijdse afrekening is aanbevolen wanneer de laatste afrekening (of, bij geen enkele,
// de aankomst) meer dan `days` dagen geleden is. Enkel voor ingecheckte gasten.
export function interimDue(r, days = 30) {
  if (!r || r.status !== "checked_in") return false;
  const ref = r.last_interim_until || r.arrival;
  if (!ref) return false;
  const due = new Date(ref.slice(0, 10) + "T00:00:00Z");
  due.setUTCDate(due.getUTCDate() + (Number(days) || 30));
  return new Date() > due;
}

export function matchesQuery(r, q) {
  const s = (q || "").trim().toLowerCase();
  if (!s) return true;
  const plates = Object.values((r.stay || {}).plates || {});
  const occ = (r.occupants || []).map((o) => `${o.first_name || ""} ${o.last_name || ""}`);
  const hay = [r.guest_name, r.booker?.first_name, r.booker?.last_name, r.booker?.nickname, r.pitch_label,
    r.first_name, r.last_name, r.email, r.phone, r.booker?.phone, r.booker?.email, ...plates, ...occ]
    .filter(Boolean).join(" ").toLowerCase();
  // elk woord moet ergens voorkomen (volgorde maakt niet uit): "Marie Nielsen" vindt "Marie DEMO Nielsen 134"
  return s.split(/\s+/).every((tok) => hay.includes(tok));
}

export function Panel({ title, action, children, testid }) {
  return (
    <div className="bg-card rounded-xl border border-border/70 shadow-sm overflow-hidden" data-testid={testid}>
      {(title || action) && (
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border">
          <h2 className="font-display font-bold text-lg text-foreground">{title}</h2>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

/** Zolang er nog gegevens onderweg zijn, melden we dat ze opgehaald worden.
 *  Pas als alles binnen is en er niets blijkt te zijn, staat er "Geen gegevens". */
export function Empty({ children }) {
  const { t } = useLang();
  const loading = useWaitingForData();
  if (loading) return <Spinner label={t("loading_data")} testid="loading-state" />;
  return (
    <div className="py-16 text-center text-muted-foreground text-sm" data-testid="empty-state">
      {children || t("no_data")}
    </div>
  );
}

/** Compacte variant voor lijstjes binnen een venster of kaart. */
export function EmptyInline({ className = "" }) {
  const { t } = useLang();
  const loading = useWaitingForData();
  return loading ? (
    <Spinner small label={t("loading_data")} testid="loading-state-inline" className={className} />
  ) : (
    <p className={`text-sm text-muted-foreground text-center py-4 ${className}`} data-testid="empty-state-inline">{t("no_data")}</p>
  );
}

/** Modern laadwieltje: conische ring met zachte gloed en pulserende kern. */
export function Spinner({ label, small = false, testid = "loading-state", className = "" }) {
  return (
    <div className={`${small ? "py-4" : "py-16"} flex flex-col items-center justify-center gap-3 ${className}`} data-testid={testid} role="status" aria-live="polite">
      <div className={`cb-spinner ${small ? "cb-spinner-sm" : ""}`}><span /></div>
      {label && <p className={`${small ? "text-xs" : "text-sm"} text-muted-foreground tracking-wide cb-spinner-label`}>{label}</p>}
    </div>
  );
}

/** true zolang er gegevens onderweg zijn: vanaf het openen van de pagina tot de eerste lading
 *  aanvragen afgerond is. Start er binnen 600 ms geen enkele aanvraag, dan is de lijst echt leeg.
 *  Daarna blijft het false, zodat een achtergrondverversing de lijst niet laat flikkeren. */
export function useWaitingForData() {
  const [waiting, setWaiting] = useState(true);
  useEffect(() => {
    let seen = pendingRequests() > 0;
    const off = onPendingChange((n) => {
      if (n > 0) seen = true;
      else if (seen) setWaiting(false);
    });
    const grace = setTimeout(() => { if (!seen && pendingRequests() === 0) setWaiting(false); }, 600);
    const cap = setTimeout(() => setWaiting(false), 15000);
    return () => { off(); clearTimeout(grace); clearTimeout(cap); };
  }, []);
  return waiting;
}

const RES = {
  reserved: { nl: "Gereserveerd", es: "Reservado", en: "Reserved", cls: "bg-amber-100 text-amber-700" },
  zoekend: { nl: "Zoekend", es: "Buscando", en: "Searching", cls: "bg-sky-100 text-sky-700" },
  checked_in: { nl: "Ingecheckt", es: "Registrado", en: "Checked in", cls: "bg-emerald-100 text-emerald-700" },
  checked_out: { nl: "Uitgecheckt", es: "Salida", en: "Checked out", cls: "bg-slate-100 text-muted-foreground" },
};

const INV = {
  open: { nl: "Open", es: "Abierta", en: "Open", cls: "bg-sky-100 text-sky-700" },
  sent: { nl: "Verzonden", es: "Enviada", en: "Sent", cls: "bg-emerald-100 text-emerald-700" },
  paid: { nl: "Betaald", es: "Pagada", en: "Paid", cls: "bg-emerald-100 text-emerald-700" },
};

export function ResBadge({ status }) {
  const { lang } = useLang();
  const m = RES[status] || RES.reserved;
  return <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${m.cls}`}>{m[lang] || m.nl}</span>;
}

export function InvBadge({ status }) {
  const { lang } = useLang();
  const m = INV[status] || INV.open;
  return <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${m.cls}`}>{m[lang] || m.nl}</span>;
}

// Volledigheid van een (voorlopige) boeking: groen "100%" zodra de gast het formulier voltooide
// (definitive=true), anders een oranje "Onvolledig".
export function CompletionBadge({ definitive, testid }) {
  const { t } = useLang();
  const done = definitive !== false;
  return (
    <span data-testid={testid} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${done ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300" : "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300"}`}>
      {done ? t("completion_complete") : t("completion_incomplete")}
    </span>
  );
}

// True wanneer een nog-niet-definitieve boeking een aanvul-link had die intussen verlopen is.
export function linkExpired(r) {
  return r && r.definitive === false && r.complete_link_exp && Number(r.complete_link_exp) < Math.floor(Date.now() / 1000);
}

// Rood vlaggetje zodra de aanvul-link van een voorlopige boeking verlopen is.
export function ExpiredLinkFlag({ reservation, testid, compact = false }) {
  const { t } = useLang();
  if (!linkExpired(reservation)) return null;
  return (
    <span data-testid={testid} title={t("link_expired")}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300">
      <Flag className="h-3.5 w-3.5" />{!compact && t("link_expired")}
    </span>
  );
}

// Voorschot-status: toont betaald/openstaand met bedrag; niets wanneer er geen betaalverzoek is.
// Bij een openstaand voorschot + onResend wordt de badge klikbaar om de betaallink opnieuw te mailen (met bevestiging).
export function DepositBadge({ reservation, testid, onResend }) {
  const { t } = useLang();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const r = reservation || {};
  const amount = r.deposit_online_amount ?? r.deposit_requested;
  if (r.deposit_online_paid) {
    return (
      <span data-testid={testid} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
        {t("deposit_paid")}{amount != null ? ` · ${money(amount)}` : ""}
      </span>
    );
  }
  if (!(r.deposit_requested > 0)) return null;
  const label = `${t("deposit_open")}${amount != null ? ` · ${money(amount)}` : ""}`;
  const cls = "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300";
  if (!onResend) return <span data-testid={testid} className={cls}>{label}</span>;
  const run = async () => {
    setBusy(true);
    try { await onResend(r); } finally { setBusy(false); setConfirm(false); }
  };
  return (
    <>
      <button type="button" data-testid={testid} onClick={() => setConfirm(true)} title={t("deposit_resend")}
        className={`${cls} cursor-pointer hover:bg-amber-200 dark:hover:bg-amber-900/60 transition-colors`}>
        {label}
      </button>
      <AlertDialog open={confirm} onOpenChange={(o) => !busy && setConfirm(o)}>
        <AlertDialogContent data-testid="deposit-resend-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">{t("deposit_resend_q")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("deposit_resend_hint").replace("{name}", r.guest_name || "").replace("{amount}", amount != null ? money(amount) : "")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="deposit-resend-cancel" disabled={busy}>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); run(); }} disabled={busy} data-testid="deposit-resend-confirm">
              {t("deposit_resend")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// Toont "Kaart gemaild · <datum>" wanneer de bevestigingskaart/invulkaart naar de gast is verstuurd.
export function CardSentBadge({ reservation, testid }) {
  const { t, lang } = useLang();
  const at = (reservation || {}).confirmation_card_sent_at;
  if (!at) return null;
  const d = new Date(at);
  const date = isNaN(d) ? "" : d.toLocaleDateString(lang === "en" ? "en-GB" : lang === "es" ? "es-ES" : "nl-BE", { day: "2-digit", month: "2-digit" });
  return (
    <span data-testid={testid} title={t("card_mailed")}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300">
      {t("card_mailed")}{date ? ` · ${date}` : ""}
    </span>
  );
}


export async function openFile(api, fileId) {
  const res = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}
